import { useMemo } from 'react';
import { isEmptyString } from '@/utils/string';
import { useLoadNew, useSettledRefetch } from '@/hooks/use-load-new';
import { useNewLogsCheck, type useLogs } from '@/services/db/activity-log/queries';
import type { ActivityLogFilters, ActivityLogTab } from './types';

const ENTRY_NOUN = { one: 'entry', other: 'entries' } as const;

type UseLogsLoadNewInput = {
  tab: ActivityLogTab;
  filters: ActivityLogFilters;
  dataWorkspaceId: string;
  /** The object whose own feed this is, if it is one. */
  cioObjectId?: string;
  logs: ReturnType<typeof useLogs>;
};

/**
 * A feed's "load new" row and toolbar status: the logs check (same tab, filters and object as
 * the list) wired into the shared `useLoadNew`.
 */
export function useLogsLoadNew({ tab, filters, dataWorkspaceId, cioObjectId, logs }: UseLogsLoadNewInput) {
  const { rows, firstPage, lastUpdated, isLoading, isFetching, isFetchingMore, refetch } = logs;
  const check = useNewLogsCheck({
    environmentId: dataWorkspaceId,
    tab,
    filters,
    cioObjectId,
    enabled: !isLoading && firstPage != null,
    seed: firstPage,
    seedUpdatedAt: lastUpdated,
  });

  const loadedIds = useMemo(() => new Set(rows.map((row) => row.log.id)), [rows]);
  const latestIds = useMemo(() => (check.data?.logs ?? []).map((log) => log.id), [check.data]);

  return useLoadNew({
    loadedIds,
    latestIds,
    latestHasMore: !isEmptyString(check.data?.meta.continuation ?? ''),
    noun: ENTRY_NOUN,
    list: { lastUpdated, isLoading, isFetching, isFetchingMore, refetch: useSettledRefetch(refetch) },
    check: { dataUpdatedAt: check.dataUpdatedAt, refetch: useSettledRefetch(check.refetch) },
  });
}
